import * as React from 'react';
import {
  MasonryEngineNode,
  MasonryEngineNodeAction,
  MasonryEngineNodeRendererProps,
} from '@leagueplatform/masonry-engine';
import { useIntl } from '@leagueplatform/locales';
import { PaginatedChatSessionList } from '../../../components/paginated-chat-session-list/paginated-chat-session-list.component';
import { registerChatListMount } from '../../chat-selection-store';
import { useItemSelection } from './use-item-selection.hook';
import { usePageChangeActions } from './use-page-change-actions.hook';

export const NODE_TYPE_PAGINATED_CHAT_SESSION_LIST = 'paginatedChatSessionList';

export type SelectedChat = { page: number; index: number };

type PaginatedChatSessionListProps = {
  hasNextPage: boolean;
  currentPage: number;
  totalPages: number;
  pageAnnouncement?: string;
  selectedChat?: SelectedChat;
  paginationStrategy?: 'page' | 'cursor';
  nextPage?: string;
  previousPage?: string;
  /** Cursor for the first page. Passed through to the view but not yet surfaced in the UI. */
  firstPage?: string;
  /** Cursor for the last page. Passed through to the view but not yet surfaced in the UI. */
  lastPage?: string;
};

type PaginatedChatSessionListActions = {
  onLoad?: MasonryEngineNodeAction;
  onPageChange?: MasonryEngineNodeAction;
};

type PaginatedChatSessionListSections = {
  items: MasonryEngineNode[];
};

type PaginatedChatSessionListNode = MasonryEngineNode<
  typeof NODE_TYPE_PAGINATED_CHAT_SESSION_LIST,
  PaginatedChatSessionListProps,
  PaginatedChatSessionListActions,
  PaginatedChatSessionListSections
>;

export type PaginatedChatSessionListRendererProps =
  MasonryEngineNodeRendererProps<PaginatedChatSessionListNode>;

export const PaginatedChatSessionListRenderer = ({
  items = [],
  currentPage,
  totalPages,
  pageAnnouncement,
  paginationStrategy,
  nextPage,
  previousPage,
  firstPage,
  lastPage,
  onPageChange,
  isReloading,
  selectedChat,
  rootElementRef,
  nodeId,
  nodeData,
}: PaginatedChatSessionListRendererProps): React.ReactElement | null => {
  const { formatMessage } = useIntl();
  const hasLoaded = items.length > 0;
  const isCursorMode = paginationStrategy === 'cursor';
  const currentPageNum = Number(currentPage) || 1;
  const totalPagesNum = Number(totalPages) || 1;

  // Node `id` drives per-item state dispatch; `sessionId` (stable per
  // schema, unlike node id) drives selection matching across reloads.
  const selectionItems = React.useMemo(
    () =>
      (nodeData.sections?.items ?? []).map((node) => ({
        nodeId: node.id,
        sessionId:
          (node.properties as { sessionId?: string } | undefined)?.sessionId ??
          node.id,
      })),
    [nodeData.sections?.items],
  );

  const { handleItemClick } = useItemSelection({
    items: selectionItems,
    selectedChat,
    currentPage: currentPageNum,
    nodeId,
    paginationStrategy,
  });

  // Register as a mounted list instance; the store clears when the
  // last instance unregisters, so overlapping route-transition
  // windows don't wipe a still-live selection.
  React.useEffect(() => registerChatListMount(), []);

  const { handleOffsetPageChange, handleCursorChange } = usePageChangeActions({
    onPageChange,
    paginationStrategy,
    nodeId,
  });

  const sharedProps = {
    ref: rootElementRef as React.RefCallback<HTMLUListElement>,
    name: formatMessage({ id: 'CHAT_HISTORY_SESSION_LIST' }),
    pageAnnouncement,
    isLoading: isReloading && hasLoaded,
    onItemClick: handleItemClick,
  };

  if (isCursorMode) {
    return (
      <PaginatedChatSessionList
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...sharedProps}
        paginationStrategy="cursor"
        nextPage={nextPage}
        previousPage={previousPage}
        firstPage={firstPage}
        lastPage={lastPage}
        onNavigate={handleCursorChange}
      >
        {items}
      </PaginatedChatSessionList>
    );
  }

  return (
    <PaginatedChatSessionList
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...sharedProps}
      currentPage={currentPageNum}
      totalPages={totalPagesNum}
      onNavigate={handleOffsetPageChange}
    >
      {items}
    </PaginatedChatSessionList>
  );
};
